import { useState, useMemo } from "react";
import { Checkbox, CheckboxChangeEvent } from "primereact/checkbox";
import { InputText } from "primereact/inputtext";

interface FilterBoxProps<T> {
  options: T[];
  value: T[];
  onChange: (e: { value: T[] }) => void;
  optionLabel?: keyof T;
  optionLabelText?: (option: T) => string;
  dataKey: keyof T;
  filter?: boolean;
  listClassName?: string;
  horizontal?: boolean;
}

function FilterBox<T extends Record<string, any>>({
  options,
  value,
  onChange,
  optionLabel,
  optionLabelText,
  dataKey,
  filter = false,
  listClassName,
  horizontal,
}: FilterBoxProps<T>) {
  const [filterValue, setFilterValue] = useState<string>("");

  const filteredOptions = useMemo(() => {
    if (!filter || !filterValue) return options;
    return options.filter((option) =>
      String(optionLabelText ? optionLabelText(option) : option[optionLabel as keyof T])
        .toLowerCase()
        .includes(filterValue.toLowerCase()),
    );
  }, [filter, filterValue, options, optionLabel, optionLabelText]);

  const handleChange = (e: CheckboxChangeEvent, option: T) => {
    const isChecked = e.checked;
    let newValue: T[];
    if (isChecked) {
      newValue = [...value, option];
    } else {
      newValue = value.filter((item) => item[dataKey] !== option[dataKey]);
    }
    onChange({ value: newValue });
  };

  return (
    <div className={`p-listbox p-component`}>
      {filter && (
        <div className="p-listbox-header">
          <div className="p-listbox-filter-container">
            <InputText
              value={filterValue}
              onChange={(e) => setFilterValue(e.target.value)}
              placeholder="Търсене"
              className="w-full"
            />
          </div>
        </div>
      )}
      <div className={`p-listbox-list-wrapper ${listClassName} overflow-y-scroll`}>
        <ul className={`p-listbox-list ${horizontal ? "flex flex-row flex-wrap" : ""}`}>
          {filteredOptions.map((option) => (
            <li
              key={String(option[dataKey])}
              className={`p-listbox-item flex align-items-center ${horizontal ? "w-4" : ""}`}
            >
              <Checkbox
                inputId={`checkbox-${String(dataKey)}-${String(option[dataKey])}`}
                onChange={(e) => handleChange(e, option)}
                checked={value.some((item) => item[dataKey] === option[dataKey])}
              />
              <label htmlFor={`checkbox-${String(dataKey)}-${String(option[dataKey])}`} className="ml-2">
                {optionLabelText ? optionLabelText(option) : String(option[optionLabel as keyof T])}
              </label>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export default FilterBox;
